import {
  Badge,
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { reactEvents } from '@bridge/reactEvents';
import { Icons } from '@texas/components/shared/Icons';
import { useTranslation } from 'react-i18next';

export interface OfferOverviewBranch {
  id: number;
  identifier: string;
  missing: boolean;
}

export interface OfferOverviewRow {
  id: string;
  offerGroupId: number;
  title: string;
  subtitle: string;
  branches: OfferOverviewBranch[];
  tiers: number[];
  owner: string;
  updated: string;
  readiness: string;
  readinessTone: 'muted' | 'success' | 'warning';
  badge?: string;
  badgeTone?: 'muted' | 'success' | 'warning';
  searchValue: string;
}

interface Props {
  rows: OfferOverviewRow[];
}

export function OfferOverviewTable({ rows }: Props) {
  const { t } = useTranslation();

  return (
    <TableContainer>
      <Table minW="820px" size="sm" variant="entityDirectoryCompact">
        <Thead>
          <Tr>
            <Th w="24%">{t('offer.offer')}</Th>
            <Th w="20%">{t('offer.coverage')}</Th>
            <Th w="18%">{t('offer.tiers')}</Th>
            <Th w="20%">{t('offer.ownerUpdated')}</Th>
            <Th w="18%">{t('offer.readiness')}</Th>
            <Th aria-label={t('general.actions')} w="40px" />
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr
              key={row.id}
              cursor="pointer"
              onClick={() =>
                reactEvents.routingChanged.dispatch({
                  to: 'variant.offers.group',
                  navObj: { offerGroupId: row.offerGroupId },
                })
              }
            >
              <Td>
                <Flex direction="column" gap={0.5}>
                  <Flex align="center" gap={2}>
                    <Text
                      fontFamily="barlow"
                      fontSize="15px"
                      fontWeight="semibold"
                    >
                      {row.title}
                    </Text>
                    {row.badge && (
                      <Badge
                        bg={getBadgeBackground(row.badgeTone)}
                        borderRadius={0}
                        color={getBadgeColor(row.badgeTone)}
                        fontSize="9px"
                      >
                        {row.badge}
                      </Badge>
                    )}
                  </Flex>
                  <Text color="entityDirectory.mutedText" fontSize="11px">
                    {row.subtitle}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex gap={1.5} wrap="wrap">
                  {row.branches.length === 0 ? (
                    <Text color="entityDirectory.mutedText">—</Text>
                  ) : (
                    row.branches.map((branch) => (
                      <Badge
                        key={branch.id}
                        bg={
                          branch.missing
                            ? 'orange.100'
                            : 'entityDirectory.successBackground'
                        }
                        borderRadius={0}
                        color={
                          branch.missing
                            ? 'orange.800'
                            : 'entityDirectory.successText'
                        }
                        fontSize="10px"
                        textTransform="uppercase"
                        _dark={
                          branch.missing
                            ? { bg: 'orange.900', color: 'orange.200' }
                            : undefined
                        }
                      >
                        {branch.missing && '+ '}
                        {branch.identifier}
                      </Badge>
                    ))
                  )}
                </Flex>
              </Td>
              <Td>
                <Flex gap={1} wrap="wrap">
                  {row.tiers.length === 0 ? (
                    <Text color="entityDirectory.mutedText">—</Text>
                  ) : (
                    row.tiers.map((tier) => (
                      <Badge
                        key={tier}
                        borderRadius={0}
                        variant="entity-directory-reference"
                      >
                        {tier.toLocaleString()}
                      </Badge>
                    ))
                  )}
                </Flex>
              </Td>
              <Td>
                <Flex direction="column" gap={0.5}>
                  <Text fontWeight="medium">{row.owner}</Text>
                  <Text color="entityDirectory.mutedText" fontSize="11px">
                    {row.updated}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center" gap={2}>
                  <Box
                    bg={getReadinessColor(row.readinessTone)}
                    borderRadius="full"
                    boxSize="6px"
                    flexShrink={0}
                  />
                  <Text
                    color={getReadinessColor(row.readinessTone)}
                    fontSize="12px"
                    fontWeight="medium"
                  >
                    {row.readiness}
                  </Text>
                </Flex>
              </Td>
              <Td px={2}>
                <Icons.ChevronRight
                  boxSize={4}
                  color="entityDirectory.mutedText"
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function getReadinessColor(tone: OfferOverviewRow['readinessTone']) {
  if (tone === 'success') return 'green.500';
  if (tone === 'warning') return 'orange.500';
  return 'entityDirectory.mutedText';
}

function getBadgeBackground(tone: OfferOverviewRow['badgeTone']) {
  if (tone === 'success') return 'entityDirectory.successBackground';
  if (tone === 'warning') return 'orange.100';
  return 'entityDirectory.badgeBackground';
}

function getBadgeColor(tone: OfferOverviewRow['badgeTone']) {
  if (tone === 'success') return 'entityDirectory.successText';
  if (tone === 'warning') return 'orange.800';
  return 'entityDirectory.mutedText';
}
